import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, ShieldCheck, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/layout/PageHeader";
import { SecretField } from "@/components/settings/SecretField";
import { api, formatApiError } from "@/lib/api";

const KEYS = [
  { id: "google_api_key", label: "Google Maps (Places API)", help: "Google Cloud Console → activer « Places API (New) » → Identifiants → Créer une clé API (200 $ de crédit mensuel offert).", helpUrl: "https://console.cloud.google.com/apis/library/places.googleapis.com" },
  { id: "hunter_api_key", label: "Hunter.io", help: "Compte gratuit : 25 recherches / mois. Tableau de bord → API.", helpUrl: "https://hunter.io/api-keys" },
  { id: "apollo_api_key", label: "Apollo.io", help: "Settings → Integrations → API → Create new key (plan gratuit limité).", helpUrl: "https://app.apollo.io/#/settings/integrations/api" },
];

const Card = ({ title, icon: Icon, children, delay = 0, id }) => (
  <section id={id} className="fade-up scroll-mt-6 rounded-lg border border-border bg-white p-6 shadow-sm" style={{ animationDelay: `${delay}ms` }}>
    <div className="mb-2 flex items-center gap-2"><Icon className="h-4 w-4 text-[#002FA7]" /><h2 className="font-display text-xl font-bold tracking-tight">{title}</h2></div>
    {children}
  </section>
);

export default function Settings() {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [keys, setKeys] = useState({ google_api_key: "", hunter_api_key: "", apollo_api_key: "" });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (settings && !form) {
      setForm({ ...settings, proxiesText: (settings.proxies || []).join("\n") });
    }
  }, [settings, form]);

  const formReady = !!form;
  useEffect(() => {
    if (formReady && window.location.hash === "#proxies") {
      document.getElementById("proxies")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [formReady]);

  const persist = async (payload, message) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", payload);
      queryClient.setQueryData(["settings"], data);
      setForm({ ...data, proxiesText: (data.proxies || []).join("\n") });
      setKeys({ google_api_key: "", hunter_api_key: "", apollo_api_key: "" });
      toast.success(message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const filledKeys = Object.fromEntries(Object.entries(keys).filter(([, v]) => v.trim()));
    persist({
      ...filledKeys,
      proxies: form.proxiesText.split("\n").map((p) => p.trim()).filter(Boolean),
      proxy_enabled: form.proxy_enabled,
      human_mode: form.human_mode,
      min_delay: Number(form.min_delay),
      max_delay: Number(form.max_delay),
      crawler_concurrency: Number(form.crawler_concurrency),
    }, "Paramètres enregistrés");
  };

  const testProxy = async () => {
    const proxy = form.proxiesText.split("\n").map((p) => p.trim()).find(Boolean);
    if (!proxy) return toast.error("Ajoutez au moins un proxy");
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-proxy", { proxy });
      data.ok ? toast.success(`Proxy opérationnel · IP sortante ${data.ip}`) : toast.error(`Proxy en échec : ${data.error || "pas de réponse"}`);
    } finally {
      setTesting(false);
    }
  };

  if (!form) return <div className="text-sm text-muted-foreground">Chargement…</div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="settings-page" className="max-w-5xl">
      <PageHeader index="04" title="Paramètres & Intégrations" subtitle="Clés API, rotation de proxies et politique anti-bot. Les clés sont stockées côté serveur et ne sont jamais renvoyées en clair." />
      <div className="space-y-6">
        <Card title="Clés API" icon={ShieldCheck}>
          {KEYS.map((k) => (
            <SecretField key={k.id} {...k} settings={settings} value={keys[k.id]} onChange={(v) => setKeys((s) => ({ ...s, [k.id]: v }))} onClear={() => persist({ [k.id]: "" }, "Clé retirée")} />
          ))}
        </Card>
        <Card title="Proxies rotatifs (HTTP/S)" icon={Wifi} delay={60} id="proxies">
          <label className="mt-3 flex items-center gap-3 text-sm">
            <Switch data-testid="proxy-enabled-switch" checked={form.proxy_enabled} onCheckedChange={(v) => set("proxy_enabled", v)} />
            Attribuer une IP différente à chaque requête de scraping
          </label>
          <Label htmlFor="proxies" className="mt-5 block">Liste des proxies (un par ligne)</Label>
          <Textarea id="proxies" data-testid="proxies-textarea" rows={5} value={form.proxiesText} onChange={(e) => set("proxiesText", e.target.value)} placeholder={"http://utilisateur:motdepasse@hote:port\nhttps://hote2:port"} className="mt-1.5 font-mono text-xs" />
          <Button type="button" variant="outline" size="sm" className="mt-3" data-testid="test-proxy-button" onClick={testProxy} disabled={testing}>
            {testing && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />} Tester le premier proxy
          </Button>
        </Card>
        <Card title="Politique de sécurité anti-bot" icon={ShieldCheck} delay={120}>
          <label className="mt-3 flex items-center gap-3 text-sm">
            <Switch data-testid="human-mode-switch" checked={form.human_mode} onCheckedChange={(v) => set("human_mode", v)} />
            <span><span className="font-semibold">Mode « Humain »</span> — délais aléatoires entre chaque requête (crawler séquentiel)</span>
          </label>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Délai min. (s)</Label><Input data-testid="min-delay-input" type="number" step="0.5" min="0.5" value={form.min_delay} onChange={(e) => set("min_delay", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Délai max. (s)</Label><Input data-testid="max-delay-input" type="number" step="0.5" min="0.5" value={form.max_delay} onChange={(e) => set("max_delay", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Crawler parallèle</Label><Input data-testid="crawler-concurrency-input" type="number" min="1" max="10" value={form.crawler_concurrency} onChange={(e) => set("crawler_concurrency", e.target.value)} /></div>
          </div>
        </Card>
        <Button data-testid="save-settings-button" onClick={save} disabled={saving} size="lg" className="transition-transform hover:-translate-y-0.5">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Enregistrer les paramètres
        </Button>
      </div>
    </div>
  );
}
